import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { CreatorPicker } from "@/components/CreatorPicker"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { TagsInput } from "@/components/TagsInput"
import { VideoFileDrop, type UploadResult } from "@/components/VideoFileDrop"
import { usePurposes } from "@/lib/queries/purposes"
import { useSetVideo } from "@/lib/queries/videos"

// ───────────────────────────────────────────────────────────────────────
// PAGE: New Video (upload)
// LEGACY: apps/admin/src/pages/UploadVideo.jsx
// PIPELINE:
//   1. POST {UPLOAD_BASE_URL}/upload_video  (multipart 'video_file')
//      → { data: { s3response, file_name } }
//   2. POST {UPLOAD_BASE_URL}/set_video      (the metadata + s3_data)
//      → { success, message }
//
// Field parity (legacy ↔ v2, set_video payload):
//   purpose              ← Purpose dropdown LABEL
//   purpose_id           ← Purpose dropdown ObjectId
//   author               ← Author Name input
//   narrater             ← Narrater Name input        (typo PRESERVED)
//   tags                 ← Tags input  (string array — same shape)
//   proverb              ← Proverb textarea
//   is_notification      ← Switch
//   notification_message ← Notification Message textarea (only if is_notif)
//   s3_data              ← VideoFileDrop result.s3response
//   file_name            ← VideoFileDrop result.file_name
//
// Behavior parity (with all M3-M5 fixes carried through):
//   - M3 / A2: Submit disabled while file is uploading (isUploading) AND
//     when no S3 data exists (no file uploaded yet).
//   - M5 / A8: real Bearer JWT in upload header (handled inside VideoFileDrop).
//   - M5 / A12: form fully reset on success (no page reload).
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  purpose_id: z.string().min(1, "Pick a purpose"),
  author: z.string().optional(),
  narrater: z.string().optional(),
  proverb: z.string().optional(),
  tags: z.array(z.string()),
  is_notification: z.boolean(),
  notification_message: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  purpose_id: "",
  author: "",
  narrater: "",
  proverb: "",
  tags: [],
  // V-Q6: first-publish defaults to "notify followers." This route is
  // CREATE-only; EditVideo.tsx never touches is_notification, so an
  // already-published video can't accidentally re-page followers.
  is_notification: true,
  notification_message: "",
}

export default function NewVideo() {
  const navigate = useNavigate()
  const purposesQ = usePurposes()
  const setVideoMut = useSetVideo()

  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  const isNotification = watch("is_notification")

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    if (!uploadResult) {
      setSubmitError("Upload a video file first.")
      return
    }
    const purposeOpt = purposesQ.data?.find((p) => p._id === values.purpose_id)
    const purposeLabel = purposeOpt?.purpose ?? ""

    try {
      const res = await setVideoMut.mutateAsync({
        s3_data: uploadResult.s3response,
        file_name: uploadResult.file_name,
        purpose: purposeLabel,
        purpose_id: values.purpose_id,
        author: values.author ?? "",
        narrater: values.narrater ?? "",
        tags: values.tags,
        proverb: values.proverb ?? "",
        is_notification: values.is_notification,
        notification_message: values.is_notification
          ? values.notification_message ?? ""
          : "",
        // Browser-generated poster frame (uploaded by VideoFileDrop).
        // Optional — falls back to no thumbnail if generation failed.
        S3_thumbnail: uploadResult.thumb_s3response,
      })
      if (!res?.success) {
        setSubmitError(res?.message || "Failed to submit video.")
        return
      }
      // Success — clear and bounce to the list (M5 / A12).
      reset(DEFAULT_VALUES)
      setUploadResult(null)
      navigate("/videos")
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to submit video.")
    }
  })

  const handleClear = () => {
    reset(DEFAULT_VALUES)
    setUploadResult(null)
    setSubmitError(null)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/videos">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to videos
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload video</CardTitle>
          <CardDescription>
            Upload the file first; metadata is saved once S3 confirms.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit} noValidate>
            {/* File upload */}
            <div className="space-y-2">
              <Label>Video / audio file</Label>
              <VideoFileDrop
                result={uploadResult}
                onResult={setUploadResult}
                onUploadingChange={setIsUploading}
                disabled={setVideoMut.isPending}
              />
            </div>

            {/* Purpose */}
            <div className="space-y-2">
              <Label htmlFor="purpose">Purpose</Label>
              <Controller
                control={control}
                name="purpose_id"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={purposesQ.isLoading}
                  >
                    <SelectTrigger id="purpose" aria-invalid={!!errors.purpose_id}>
                      <SelectValue placeholder="Choose a purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      {purposesQ.data?.map((p) => (
                        <SelectItem key={p._id} value={p._id}>
                          {p.purpose}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.purpose_id && (
                <p className="text-xs text-destructive">{errors.purpose_id.message}</p>
              )}
            </div>

            {/* Author — picker against the Creators directory */}
            <div className="space-y-2">
              <Label htmlFor="author">Author</Label>
              <Controller
                control={control}
                name="author"
                render={({ field }) => (
                  <CreatorPicker
                    id="author"
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Pick or create the author"
                  />
                )}
              />
            </div>

            {/* Narrater (legacy field name preserved) — same picker */}
            <div className="space-y-2">
              <Label htmlFor="narrater">Narrater</Label>
              <Controller
                control={control}
                name="narrater"
                render={({ field }) => (
                  <CreatorPicker
                    id="narrater"
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Pick or create the narrator"
                  />
                )}
              />
            </div>

            {/* Proverb */}
            <div className="space-y-2">
              <Label htmlFor="proverb">Proverb</Label>
              <Textarea id="proverb" rows={4} {...register("proverb")} />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <Controller
                control={control}
                name="tags"
                render={({ field }) => (
                  <TagsInput value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Notification toggle + message */}
            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="is_notification">Send notification</Label>
                  <p className="text-xs text-muted-foreground">
                    Push to subscribed users when the video goes live.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_notification"
                  render={({ field }) => (
                    <Switch
                      id="is_notification"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              {isNotification && (
                <div className="space-y-2">
                  <Label htmlFor="notification_message">Notification message</Label>
                  <Textarea
                    id="notification_message"
                    rows={3}
                    {...register("notification_message")}
                  />
                </div>
              )}
            </div>

            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                disabled={
                  isUploading || !uploadResult || setVideoMut.isPending
                }
              >
                {(setVideoMut.isPending || isUploading) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isUploading
                  ? "Uploading…"
                  : setVideoMut.isPending
                    ? "Submitting…"
                    : "Submit video"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClear}
                disabled={setVideoMut.isPending || isUploading}
              >
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
